import cn from 'classnames';
import { useTranslation } from 'next-i18next';
import { usePdp } from '@context/PdpContext';
import useFormattedPrice from '@hooks/useFormattedPrice';
import IconWrapper from '@components/ui/Icons';
import { useUI } from '@context/GlobalUIContext';
import useCustomer from '@adapters/bigcommerce/hooks/customer/useCustomer';
import { getCustomerGroupMessage } from '@utils/cart/getCustomerGroupMessage';
import PdpPriceContainerView from './PdpPriceContainerView';

import styles from './ProductPrice.module.scss';
import {
  FEATURE_FLAG_PDP_PRICE_CONTAINER,
  FEATURE_FLAG_PRODUCT_TAGS,
} from '@config/feature-flag';

const ProductPrice = () => {
  const { t } = useTranslation('common');
  const { customer } = useCustomer();
  const {
    price,
    tagInfoModalPath,
    inCartDiscount,
    hasCustomerGroupPriceDifference,
  } = usePdp();
  const { openTagInfoModal } = useUI();

  const { originalPriceString, priceString } = useFormattedPrice(price);
  const isInCartDiscountTag = inCartDiscount && FEATURE_FLAG_PRODUCT_TAGS;

  const customerGroupMessage = getCustomerGroupMessage(
    t,
    customer?.customerGroupId ?? 0
  );
  const showGroupLabel =
    Boolean(customerGroupMessage) && hasCustomerGroupPriceDifference;

  if (!price) return null;

  if (FEATURE_FLAG_PDP_PRICE_CONTAINER) {
    return (
      <PdpPriceContainerView
        price={price}
        priceString={priceString}
        originalPriceString={originalPriceString}
        showGroupLabel={showGroupLabel}
        customerGroupMessage={customerGroupMessage}
        isInCartDiscountTag={Boolean(isInCartDiscountTag)}
        tagInfoModalPath={tagInfoModalPath}
      />
    );
  }

  return (
    <div>
      {showGroupLabel && (
        <div className={styles.memberGroupLabel}>{customerGroupMessage}</div>
      )}
      <div className={styles.priceContainer}>
        <strong
          className={cn(styles.actualPrice, {
            [styles.hasInCartDiscountTag]: isInCartDiscountTag,
            [styles.hasCustomerGroupPrice]: showGroupLabel,
          })}
          data-testid="actual-price"
        >
          {priceString}
        </strong>
        {isInCartDiscountTag && (
          <button
            className={styles.infoIcon}
            onClick={() => openTagInfoModal({ path: tagInfoModalPath })}
          >
            <IconWrapper icon="info" width={20} />
          </button>
        )}
        {originalPriceString && originalPriceString !== priceString && (
          <>
            <span className={styles.basePrice} data-testid="original-price">
              {originalPriceString}
            </span>
          </>
        )}
      </div>
    </div>
  );
};

export default ProductPrice;
